import { type ComponentProps, ReactElement, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { List, useListRef } from 'react-window'
import { Box, CircularProgress, useTheme } from '@mui/material'
import { usePositionGuidToWallGuid, useResultsSelection } from '@editor/hooks'
import { useResults2FokusEffect } from '@editor/pages/Results2/hooks/useResults2FokusEffect'
import { useEditorViewStore } from '@editor/stores'
import { EmptyState } from '@ui2/feedback'
import { Results2ElementListFormValues, Results2ElementListProps } from '..'
import Results2ListPaneActionsFooter from '../../Results2ListPaneActionsFooter'
import { useAnchorCommentAutosave } from '../hooks/useAnchorCommentAutosave'
import { useElementListFilters } from '../hooks/useElementListFilters'
import { useGroupingDragAndDrop } from '../hooks/useGroupingDragAndDrop'
import { type ListViewState } from '../hooks/useListViewState'
import { usePositionGroupingAutosave } from '../hooks/usePositionGroupingAutosave'
import { useRenderedRows } from '../hooks/useRenderedRows'
import { useRowSelection } from '../hooks/useRowSelection'
import { useScrollSelectedRowIntoView } from '../hooks/useScrollSelectedRowIntoView'
import {
  DEFAULT_SORT_DIR,
  DEFAULT_SORT_MODES,
  type Results2ListItem,
  type SortMode,
} from '../types'
import { ListStatusBar } from './ListStatusBar'
import { ListToolbar } from './ListToolbar'
import VirtualizedResults2List, { type VirtualizedListRowProps } from './VirtualizedResults2List'

type Results2ElementListInnerProps = Omit<Results2ElementListProps, 'items'> & {
  // View state (sort, filters, expansion) is owned by the parent — above the keyed Form2 — so it
  // survives the form remounts triggered by incoming `items` changes (e.g. the autosave echo).
  viewState: ListViewState
}

const Results2ElementListInner = ({
  elementType,
  sortModes,
  selectedId,
  selectedIds,
  onSelect,
  onSelectionChange,
  multiSelectEnabled = false,
  groupingEnabled = true,
  isLoading = false,
  emptyMessage = 'Keine Daten verfügbar',
  onCalculate,
  onBlockpad,
  blockpadSlot,
  showBlockpadButton = true,
  calculateSlot,
  onAbort,
  showAbort = false,
  onCopyParentCrossSection,
  copyParentCrossSectionVisibleByChildId,
  copyParentCrossSectionLoadingChildId,
  showCommentColumn = true,
  showExtendedCommentColumn = true,
  qsFullWidth = false,
  commentPlaceholder,
  commentSuggestionKind = 'position',
  anchorCommentAutosave = false,
  showThreeDTargetButtons = true,
  hideExportButton = false,
  metricSortLabel,
  etaCSortEnabled = true,
  qsSortOption,
  qsFilterKind,
  showShearAnchorPositionFilter = false,
  fokusCamera,
  onFokusCameraChange,
  fokusTransparency,
  onFokusTransparencyChange,
  disableForm = false,
  viewState,
}: Results2ElementListInnerProps): ReactElement => {
  const theme = useTheme()
  const { control, getValues, setValue } = useFormContext<Results2ElementListFormValues>()
  const items = useWatch({ control, name: 'items' })
  const virtualListRef = useListRef()

  const {
    sortMode,
    setSortMode,
    sortDir,
    setSortDir,
    filterText,
    setFilterText,
    filters,
    setFilters,
    expanded,
    setExpanded,
  } = viewState

  // A list only offers some sort modes, and the stored one survives a tab switch:
  // fall back to η without mutating state (avoids unnecessary re-renders during
  // view switches). "tree" additionally needs grouping, the trailing-metric and
  // η c,90 modes their per-list gates.
  const offeredSortModes = sortModes ?? DEFAULT_SORT_MODES
  const sortModeIsOffered =
    offeredSortModes.includes(sortMode) &&
    (groupingEnabled || sortMode !== 'tree') &&
    (metricSortLabel !== undefined || sortMode !== 'trailingMetric') &&
    (etaCSortEnabled || sortMode !== 'etaC')
  const effectiveSortMode: SortMode = sortModeIsOffered ? sortMode : 'eta'
  const isTree = groupingEnabled && effectiveSortMode === 'tree'

  // Clicking the active mode again flips its direction; picking a new mode resets to its default.
  const handleSortModeChange = useCallback(
    (next: SortMode) => {
      if (next === effectiveSortMode) {
        if (next !== 'tree') {
          // The visible mode may be a fallback from a stale stored one: sync the stored mode to
          // the visible one so the original list doesn't restore its stale ordering.
          if (sortMode !== next) setSortMode(next)
          setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'))
        }
        return
      }
      setSortMode(next)
      setSortDir(DEFAULT_SORT_DIR[next])
    },
    [effectiveSortMode, sortMode, setSortMode, setSortDir],
  )

  useResults2FokusEffect(fokusTransparency, selectedId, selectedIds)

  const {
    onlyWarn,
    setOnlyWarn,
    filter3d,
    setFilter3d,
    visibleItems,
    crossSectionOptions,
    warnCount,
  } = useElementListFilters({ items, isTree, showCommentColumn, filterText, filters })

  const { rendered, renderedIndexById, itemGuidToIndex, childCountByParent, toggleExpand } =
    useRenderedRows({
      items,
      visibleItems,
      isTree,
      sortMode: effectiveSortMode,
      sortDir,
      expanded,
      setExpanded,
    })

  const selectedIdSet = useMemo(
    () => new Set(selectedIds ?? (selectedId ? [selectedId] : [])),
    [selectedId, selectedIds],
  )

  const { handleRowClick, selectRow } = useRowSelection({
    multiSelectEnabled,
    rendered,
    selectedId,
    selectedIds,
    selectedIdSet,
    onSelect,
    onSelectionChange,
  })

  const setItems = useCallback(
    (updater: (current: Results2ListItem[]) => Results2ListItem[]) => {
      const current = getValues('items') ?? []
      setValue('items', updater(current), { shouldDirty: true, shouldTouch: true })
    },
    [getValues, setValue],
  )

  const { dragIds, dropTarget, startPointerDrag, canShowAddToGroupButton, addToSelectedGroup } =
    useGroupingDragAndDrop({
      disableForm,
      groupingEnabled,
      isTree,
      items,
      selectedId,
      selectedIds,
      selectedIdSet,
      childCountByParent,
      setItems,
      selectRow,
    })

  // The anchors list saves its comments through the manual-loads endpoint instead of the
  // position grouping; both hooks stay mounted (stable hook order) and gate on the mode.
  const positionAutosave = usePositionGroupingAutosave({
    items,
    elementType,
    disableForm: disableForm || anchorCommentAutosave,
  })
  const anchorAutosave = useAnchorCommentAutosave({
    items,
    enabled: anchorCommentAutosave,
    disableForm,
  })
  const { isSaving, dirty, flushAutosave } = anchorCommentAutosave
    ? anchorAutosave
    : positionAutosave

  const { handleRowsRendered } = useScrollSelectedRowIntoView({
    isLoading,
    isTree,
    items,
    rendered,
    renderedIndexById,
    setExpanded,
    selectedId,
    selectedIds,
    virtualListRef,
  })

  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  const { addResultsSelection } = useResultsSelection()
  const setResultsWallSubTab = useEditorViewStore(state => state.setResultsWallSubTab)

  const visibleTopLevelIds = useMemo(() => {
    // Flat modes: every visible row is a peer — frame/select all of them in 3D.
    if (!isTree) return visibleItems.map(i => i.id)
    return visibleItems.filter(i => !i.parent).map(i => i.id)
  }, [visibleItems, isTree])

  const handleThreeDTargetBauteile = useCallback(() => {
    if (visibleTopLevelIds.length === 0) return
    addResultsSelection(visibleTopLevelIds)
  }, [addResultsSelection, visibleTopLevelIds])

  const handleThreeDTargetWaende = useCallback(() => {
    if (visibleTopLevelIds.length === 0) return
    const wallIds = visibleTopLevelIds
      .map(id => positionGuidToWallGuid[id])
      .filter((id): id is string => Boolean(id))
    if (wallIds.length === 0) return
    // Switch to Wand → Wände, then restore selection as multi-select.
    setResultsWallSubTab('wall-assemblies')
    addResultsSelection(wallIds)
  }, [addResultsSelection, positionGuidToWallGuid, setResultsWallSubTab, visibleTopLevelIds])

  const rowProps = useMemo<VirtualizedListRowProps>(
    () => ({
      rendered,
      isTree,
      itemGuidToIndex,
      expanded,
      selectedIdSet,
      dragIds,
      dropTarget,
      hideExportButton,
      disableForm,
      showCommentColumn,
      showExtendedCommentColumn,
      qsFullWidth,
      commentPlaceholder,
      commentSuggestionKind,
      copyParentCrossSectionVisibleByChildId,
      copyParentCrossSectionLoadingChildId: copyParentCrossSectionLoadingChildId ?? null,
      onCopyParentCrossSection,
      canShowAddToGroupButton,
      toggleExpand,
      onRowClick: handleRowClick,
      startPointerDrag,
      addToSelectedGroup,
    }),
    [
      rendered,
      isTree,
      itemGuidToIndex,
      expanded,
      selectedIdSet,
      dragIds,
      dropTarget,
      hideExportButton,
      disableForm,
      showCommentColumn,
      showExtendedCommentColumn,
      qsFullWidth,
      commentPlaceholder,
      commentSuggestionKind,
      copyParentCrossSectionVisibleByChildId,
      copyParentCrossSectionLoadingChildId,
      onCopyParentCrossSection,
      canShowAddToGroupButton,
      toggleExpand,
      handleRowClick,
      startPointerDrag,
      addToSelectedGroup,
    ],
  )

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ListToolbar
        filterText={filterText}
        onFilterTextChange={setFilterText}
        filters={filters}
        onFiltersChange={setFilters}
        crossSectionOptions={crossSectionOptions}
        showExportedFilter={!hideExportButton}
        showShearAnchorPositionFilter={showShearAnchorPositionFilter}
        sortMode={effectiveSortMode}
        sortDir={sortDir}
        onSortModeChange={handleSortModeChange}
        groupingEnabled={groupingEnabled}
        metricSortLabel={metricSortLabel}
        etaCSortEnabled={etaCSortEnabled}
        qsSortOption={qsSortOption}
        qsFilterKind={qsFilterKind}
        sortModes={offeredSortModes}
      />

      <ListStatusBar
        onlyWarnings={onlyWarn}
        onOnlyWarningsChange={setOnlyWarn}
        warnCount={warnCount}
        isSaving={isSaving}
        dirty={dirty}
        onSaveNow={flushAutosave}
        visibleCount={rendered.length}
        totalCount={items.length}
      />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <CircularProgress size={24} thickness={4} />
          </Box>
        ) : rendered.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <EmptyState sx={{ fontSize: 13 }}>{emptyMessage}</EmptyState>
          </Box>
        ) : (
          <VirtualizedResults2List
            listRef={virtualListRef as ComponentProps<typeof List>['listRef']}
            rowProps={rowProps}
            onRowsRendered={handleRowsRendered}
          />
        )}
      </Box>

      <Results2ListPaneActionsFooter
        filter3d={filter3d}
        onFilter3dChange={setFilter3d}
        fokusCamera={fokusCamera}
        onFokusCameraChange={onFokusCameraChange}
        fokusTransparency={fokusTransparency}
        onFokusTransparencyChange={onFokusTransparencyChange}
        onThreeDTargetBauteile={showThreeDTargetButtons ? handleThreeDTargetBauteile : undefined}
        onThreeDTargetWaende={showThreeDTargetButtons ? handleThreeDTargetWaende : undefined}
        showBlockpadButton={showBlockpadButton}
        onBlockpad={onBlockpad}
        blockpadSlot={blockpadSlot}
        calculateSlot={calculateSlot}
        onCalculate={onCalculate}
        onAbort={onAbort}
        showAbort={showAbort}
        isLoading={isLoading}
        disableForm={disableForm}
      />
    </Box>
  )
}

export default Results2ElementListInner
